import { ReactNode } from 'react'

import { getChainInfo } from '@cowprotocol/common-const'
import { SupportedChainId, TargetChainId } from '@cowprotocol/cow-sdk'
import { Badge, BadgeTypes } from '@cowprotocol/ui'

import { Trans } from '@lingui/react/macro'

import { ActiveRowLinks } from './ActiveRowLinks/ActiveRowLinks.pure'
import * as styledEl from './NetworksList.styled'
import { getLogo } from './NetworksList.utils'

import { useDeprecatedChains } from '../../hooks/useDeprecatedChains'
import { ChainStatusBadge } from '../ChainStatusBadge/ChainStatusBadge.pure'

const NEW_NETWORK_IDS: Set<TargetChainId> = new Set([])

export interface NetworksListProps {
  currentChainId: SupportedChainId | null
  isDarkMode: boolean
  availableChains: TargetChainId[]
  onSelectChain(targetChainId: TargetChainId): void
}

export function NetworksList({
  currentChainId,
  isDarkMode,
  availableChains,
  onSelectChain,
}: NetworksListProps): ReactNode {
  const deprecatedChains = useDeprecatedChains()

  return (
    <>
      {availableChains.map((targetChainId: TargetChainId) => {
        const info = getChainInfo(targetChainId)
        const { label, logo } = info

        const isActive = targetChainId === currentChainId
        const logoUrl = getLogo(isDarkMode, isActive, logo.dark, logo.light)
        const isNewNetwork = NEW_NETWORK_IDS.has(targetChainId)
        const isDeprecatedNetwork = deprecatedChains.has(targetChainId)

        const rowContent = (
          <styledEl.FlyoutRow
            key={targetChainId}
            type="button"
            onClick={() => onSelectChain(targetChainId)}
            $active={isActive}
          >
            <styledEl.Logo src={logoUrl} />
            <styledEl.NetworkLabel color={info.color}>{label}</styledEl.NetworkLabel>

            {isDeprecatedNetwork && (
              <Badge
                type={isDarkMode ? BadgeTypes.ALERT : isActive ? BadgeTypes.ALERT2 : BadgeTypes.ALERT}
                style={isActive ? { marginRight: '10px' } : undefined}
              >
                <Trans>READ-ONLY</Trans>
              </Badge>
            )}

            {isNewNetwork && !isDeprecatedNetwork && (
              <Badge type={BadgeTypes.ALERT2} style={isActive ? { marginRight: '10px' } : undefined}>
                <Trans>NEW</Trans>
              </Badge>
            )}

            {!isDeprecatedNetwork && (
              <ChainStatusBadge chainId={targetChainId} style={isActive ? { marginRight: '10px' } : undefined} />
            )}

            {isActive && <styledEl.FlyoutRowActiveIndicator $active />}
          </styledEl.FlyoutRow>
        )

        if (!isActive) {
          return rowContent
        }

        return (
          <styledEl.ActiveRowWrapper key={targetChainId}>
            {rowContent}
            <ActiveRowLinks
              bridge={info.bridge}
              explorer={info.explorer}
              explorerTitle={info.explorerTitle}
              helpCenterUrl={info.helpCenterUrl}
              targetChainId={targetChainId}
            />
          </styledEl.ActiveRowWrapper>
        )
      })}
    </>
  )
}
